import React, { useState, useRef } from 'react';
import './JournalPortal.css';

const JournalPortal = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);
  const [view, setView] = useState('upload'); // 'upload' or 'results'
  const fileInputRef = useRef(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Handle file selection
  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    const pdfFiles = selectedFiles.filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length !== selectedFiles.length) {
      alert('Only PDF files are accepted');
    }
    
    setFiles(prev => [...prev, ...pdfFiles]);
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    const pdfFiles = droppedFiles.filter(file => file.type === 'application/pdf');
    
    setFiles(prev => [...prev, ...pdfFiles]);
  };

  // Remove file from queue
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Upload manuscripts
  const handleUpload = async () => {
    if (files.length === 0) {
      alert('Please select at least one PDF');
      return;
    }

    setUploading(true);
    const uploadResults = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('title', file.name);

        const response = await fetch(`${API_BASE}/api/journals/upload`, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }

        const data = await response.json();
        uploadResults.push({
          filename: file.name,
          manuscript_id: data.manuscript_id,
          status: 'processing'
        });

        // Poll for results
        pollForAnalysis(data.manuscript_id, file.name, uploadResults);
      } catch (error) {
        uploadResults.push({
          filename: file.name,
          status: 'error',
          message: error.message
        });
      }
    }

    setResults(uploadResults);
    setFiles([]);
    setView('results');
    setUploading(false);
  };

  // Poll for analysis completion
  const pollForAnalysis = async (manuscriptId, filename, uploadResults) => {
    const maxAttempts = 30; // 30 seconds max
    let attempts = 0;

    const poll = async () => {
      attempts++;

      try {
        const response = await fetch(
          `${API_BASE}/api/journals/manuscripts/${manuscriptId}/status`
        );
        
        if (!response.ok) throw new Error('Status check failed');

        const data = await response.json();

        if (data.status === 'completed') {
          // Get full analysis
          const analysisResponse = await fetch(
            `${API_BASE}/api/journals/manuscripts/${manuscriptId}/analysis`
          );
          
          if (analysisResponse.ok) {
            const analysis = await analysisResponse.json();
            const result = uploadResults.find(r => r.filename === filename);
            if (result) {
              result.status = 'completed';
              result.analysis = analysis;
            }
            setResults([...uploadResults]);
          }
        } else if (data.status === 'failed') {
          const result = uploadResults.find(r => r.filename === filename);
          if (result) {
            result.status = 'error';
            result.message = data.error_message || 'Analysis failed';
          }
          setResults([...uploadResults]);
        } else if (attempts < maxAttempts) {
          // Keep polling
          setTimeout(poll, 1000);
        } else {
          // Timeout
          const result = uploadResults.find(r => r.filename === filename);
          if (result) {
            result.status = 'timeout';
            result.message = 'Analysis took too long';
          }
          setResults([...uploadResults]);
        }
      } catch (error) {
        const result = uploadResults.find(r => r.filename === filename);
        if (result) {
          result.status = 'error';
          result.message = error.message;
        }
        setResults([...uploadResults]);
      }
    };

    poll();
  };

  // Export results to CSV
  const exportToCSV = () => {
    const csvContent = [
      ['Filename', 'Desk Reject?', 'Confidence', 'Key Issues', 'Status'],
      ...results.map(r => [
        r.filename,
        r.analysis?.desk_reject_recommendation || 'N/A',
        r.analysis?.confidence || 'N/A',
        r.analysis?.key_message?.substring(0, 100) || 'N/A',
        r.status
      ])
    ]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `manuscript-analysis-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="journal-portal">
      <div className="portal-header">
        <h1>Skrutini — Journal Manuscript Analysis</h1>
        <p>Upload PDFs for AI-powered peer review screening</p>
      </div>

      {view === 'upload' ? (
        <div className="upload-section">
          {/* Upload Area */}
          <div
            className="upload-area"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <h3>Upload Manuscripts</h3>
            <p>Drag PDF files here or click to browse</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf"
              onChange={handleFileSelect}
              hidden
            />
          </div>

          {/* File Queue */}
          {files.length > 0 && (
            <div className="file-queue">
              <h3>{files.length} file(s) selected</h3>
              <ul>
                {files.map((file, idx) => (
                  <li key={idx}>
                    <span>{file.name}</span>
                    <button
                      onClick={() => removeFile(idx)}
                      className="remove-btn"
                      aria-label="Remove file"
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Upload Button */}
          <div className="action-buttons">
            <button
              onClick={handleUpload}
              disabled={files.length === 0 || uploading}
              className="btn-primary"
            >
              {uploading ? 'Analyzing...' : 'Upload & Analyze'}
            </button>
          </div>
        </div>
      ) : (
        // Results View
        <div className="results-section">
          <div className="results-header">
            <h2>Analysis Results</h2>
            <div className="results-actions">
              <button onClick={() => setView('upload')} className="btn-secondary">
                Upload More
              </button>
              <button onClick={exportToCSV} className="btn-secondary">
                Export CSV
              </button>
            </div>
          </div>

          <div className="results-list">
            {results.map((result, idx) => (
              <div key={idx} className={`result-card status-${result.status}`}>
                <div className="result-header">
                  <span className="filename">{result.filename}</span>
                  <span className={`status-badge ${result.status}`}>
                    {result.status === 'completed' ? '✓ Done' : result.status}
                  </span>
                </div>

                {result.status === 'completed' && result.analysis && (
                  <div className="result-content">
                    <div className="recommendation">
                      <strong>Recommendation:</strong>
                      <span className={`rec-${result.analysis.desk_reject_recommendation}`}>
                        {result.analysis.desk_reject_recommendation.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </div>

                    <div className="confidence">
                      <strong>Confidence:</strong>
                      <div className="confidence-bar">
                        <div
                          className="confidence-fill"
                          style={{ width: `${result.analysis.confidence * 100}%` }}
                        />
                      </div>
                      <span>{(result.analysis.confidence * 100).toFixed(0)}%</span>
                    </div>

                    <div className="key-message">
                      <strong>Key Findings:</strong>
                      <p>{result.analysis.key_message}</p>
                    </div>

                    {result.analysis.red_flags && result.analysis.red_flags.length > 0 && (
                      <div className="red-flags">
                        <strong>Flagged Issues:</strong>
                        <ul>
                          {result.analysis.red_flags.map((flag, i) => (
                            <li key={i} className={`flag-${flag.severity}`}>
                              <span className="category">{flag.category}</span>
                              <span className="severity">{flag.severity}</span>
                              <span className="issue">{flag.issue}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {result.status === 'error' && (
                  <div className="error-message">
                    <strong>Error:</strong> {result.message}
                  </div>
                )}

                {result.status === 'processing' && (
                  <div className="processing">
                    <div className="spinner" />
                    <span>Analyzing manuscript...</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalPortal;
